import { Injectable, signal } from '@angular/core';

export interface EngineeringOption {
  label: string;
  value: string;
  flagged: boolean;
}

type Operator = '>' | '>=' | '<' | '<=' | '==' | '!=';

interface Condition {
  operator: Operator;
  value: number;
}

interface CompoundConditions {
  [subKey: string]: Condition;
}

interface AbNormals {
  [key: string]: Condition | CompoundConditions;
}

interface TagIds {
  [key: string]: number;
}

@Injectable({
  providedIn: 'root'
})
export class EngineeringFlaggingService {
  seismicOptions= signal< EngineeringOption[]>([
    {
      label: 'A',
      value: 'A',
      flagged: true
    },
    {
      label: 'B',
      value: 'B',
      flagged: true
    },
    {
      label: 'C',
      value: 'C',
      flagged: false
    },
    {
      label: 'D',
      value: 'D',
      flagged: false
    },
    {
      label: 'E',
      value: 'E',
      flagged: true
    }
  ]);

  exposureOptions= signal< EngineeringOption[]>([
    { 
      label: 'B',
      value: 'B',
      flagged: true
    },
    {
      label: 'C',
      value: 'C',
      flagged: false
    },
    {
      label: 'D',
      value: 'D',
      flagged: false
    }
  ]);

  windTypes = signal< string[]>(['ASD', 'ULT']);
  snowTypes = signal< string[]>(['Ground', 'Roof']);

  private abNormals: AbNormals = {
    snow: {
      Ground: { operator: '>', value: 50 },
      Roof: { operator: '>', value: 40 }
    },
    wind: {
      ULT: { operator: '>', value: 115 },
      ASD: { operator: '>', value: 90 }
    },
    soil: { operator: '!=', value: 1500 },
    frost: { operator: '>', value: 18 }
  };

  private tagIds: TagIds = {
    exposure: 17,
    seismic: 20,
    soil: 18,
    windload: 16,
    frost: 19,
    snow: 15
  };

  constructor() { }

  public isAbnormal(field: string, value: number, contextType?: string): boolean {
    const conditionSet = this.abNormals[field];
    if (!conditionSet) return false;

    if (this.isCompoundCondition(conditionSet)) {
      if (!contextType || !conditionSet[contextType]) return false;
      const condition = conditionSet[contextType];
      return this.compare(value, condition.operator, condition.value);
    } else {
      return this.compare(value, conditionSet.operator, conditionSet.value);
    }
  }

  public getTriggeredTags(formValues: { [key: string]: any }): number[] {
    const triggeredTags: number[] = [];

    for (const key in this.abNormals) {
      const conditionSet = this.abNormals[key];

      if (this.isCompoundCondition(conditionSet)) {
        // compound field, e.g. snowload and snowType
        let value: number;
        let subKey: string;

        if (key === 'snow') {
          value = formValues['snowload'];
          subKey = formValues['snowType'];
        } else if (key === 'wind') {
          value = formValues['windload'];
          subKey = formValues['windtype'];
        } else {
          continue; // skip unknown compound types
        }

        if (!value || !subKey) continue;

        const condition = conditionSet[subKey];
        if (condition && this.compare(value, condition.operator, condition.value)) {
          triggeredTags.push(this.tagIds[key]);
        }

      } else {
        // simple condition
        const value = formValues[key];
        if (value === undefined) continue;

        if (this.compare(value, conditionSet.operator, conditionSet.value)) {
          triggeredTags.push(this.tagIds[key]);
        }
      }
    }

    return triggeredTags;
  }

  private compare(value: number, operator: Operator, target: number): boolean {
    switch (operator) {
      case '>': return value > target;
      case '>=': return value >= target;
      case '<': return value < target;
      case '<=': return value <= target;
      case '==': return value === target;
      case '!=': return value !== target;
      default: throw new Error(`Unsupported operator: ${operator}`);
    }
  }

  private isCompoundCondition(obj: any): obj is CompoundConditions {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      Object.values(obj).every(
        (val) =>
          val &&
          typeof val === 'object' &&
          'operator' in val &&
          typeof (val as any).operator === 'string'
      )
    );
  }
  
}
